import React from "react";
import * as PropTypes from "prop-types";
import { contraceptiveMethods, indicators } from "../constants";
import { trimToRange, trimYearToRange } from "../utilities";
import * as api from "../api/api";
import Targets from "../components/Targets/Targets";

const createInitialState = () => ({
    indicatorProbabilityParameters: {
        indicator: indicators[0].value,
        maritalStatus: "married",
        year: undefined,
        direction: "greaterThan",
        level: undefined,
    },
    indicatorLevelParameters: {
        indicator: indicators[0].value,
        maritalStatus: "married",
        year: undefined,
        probability: undefined,
        direction: "greaterThan",
    },
    populationProbabilityParameters: {
        delta: "total",
        maritalStatus: "married",
        contraceptiveMethod: contraceptiveMethods[0].value,
        relativeToYear: undefined,
        year: undefined,
        direction: "greaterThan",
        level: undefined,
    },
    populationLevelParameters: {
        delta: "total",
        maritalStatus: "married",
        contraceptiveMethod: contraceptiveMethods[0].value,
        relativeToYear: undefined,
        year: undefined,
        probability: undefined,
        direction: "greaterThan",
    },
    calculatedAnswers: {
        indicatorProbability: undefined,
        indicatorLevel: undefined,
        populationProbability: undefined,
        populationLevel: undefined,
    },
    calculating: false,
});

const createPayload = (state) => {
    const payload = {};

    if (state.indicatorProbabilityParameters.year && !isNaN(state.indicatorProbabilityParameters.level)) {
        payload.indicatorProbabilityParameters = {
            ...state.indicatorProbabilityParameters,
            level: state.indicatorProbabilityParameters.level / 100,
        };
    }

    if (state.indicatorLevelParameters.year && !isNaN(state.indicatorLevelParameters.probability)) {
        payload.indicatorLevelParameters = state.indicatorLevelParameters;
    }

    if (state.populationProbabilityParameters.year && !isNaN(state.populationProbabilityParameters.level)) {
        payload.populationProbabilityParameters = state.populationProbabilityParameters;
    }

    if (state.populationLevelParameters.year && !isNaN(state.populationLevelParameters.probability)) {
        payload.populationLevelParameters = state.populationLevelParameters;
    }

    return payload;
};

const TargetsContainer = (props) => {
    const [state, setState] = React.useState(createInitialState());

    const signal = api.getSignal();

    const setStateSafely = (newState) => {
        setState((prevState) => ({
            ...prevState,
            ...newState,
        }));
    };

    React.useEffect(() => {
        return () => {
            signal.cancel();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    //------------------------------------------------------------------------------------------------------------------

    const onSelectIndicatorProbabilityIndicator = (indicator) => {
        setStateSafely({
            indicatorProbabilityParameters: {
                ...state.indicatorProbabilityParameters,
                indicator: indicator,
            },
        });
    };

    const onSelectIndicatorProbabilityMaritalStatus = (maritalStatus) => {
        setStateSafely({
            indicatorProbabilityParameters: {
                ...state.indicatorProbabilityParameters,
                maritalStatus: maritalStatus,
            },
        });
    };

    const onChangeIndicatorProbabilityYear = (year, enforceRange) => {
        year = trimYearToRange(year, enforceRange);

        setStateSafely({
            indicatorProbabilityParameters: {
                ...state.indicatorProbabilityParameters,
                year: year,
            },
        });
    };

    const onSelectIndicatorProbabilityDirection = (direction) => {
        setStateSafely({
            indicatorProbabilityParameters: {
                ...state.indicatorProbabilityParameters,
                direction: direction,
            },
        });
    };

    const onChangeIndicatorProbabilityLevel = (level) => {
        level = trimToRange(level, 0, 100);

        setStateSafely({
            indicatorProbabilityParameters: {
                ...state.indicatorProbabilityParameters,
                level: level,
            },
        });
    };

    //------------------------------------------------------------------------------------------------------------------

    const onSelectIndicatorLevelIndicator = (indicator) => {
        setStateSafely({
            indicatorLevelParameters: {
                ...state.indicatorLevelParameters,
                indicator: indicator,
            },
        });
    };

    const onSelectIndicatorLevelMaritalStatus = (maritalStatus) => {
        setStateSafely({
            indicatorLevelParameters: {
                ...state.indicatorLevelParameters,
                maritalStatus: maritalStatus,
            },
        });
    };

    const onChangeIndicatorLevelYear = (year, enforceRange) => {
        year = trimYearToRange(year, enforceRange);

        setStateSafely({
            indicatorLevelParameters: {
                ...state.indicatorLevelParameters,
                year: year,
            },
        });
    };

    const onChangeIndicatorLevelProbability = (probability) => {
        if (!probability) {
            probability = "";
        } else if (!probability.match(/^(0?(\.\d*)?|1)$/)) {
            probability = state.indicatorLevelParameters.probability;
        }

        setStateSafely({
            indicatorLevelParameters: {
                ...state.indicatorLevelParameters,
                probability: probability,
            },
        });
    };

    const onSelectIndicatorLevelDirection = (direction) => {
        setStateSafely({
            indicatorLevelParameters: {
                ...state.indicatorLevelParameters,
                direction: direction,
            },
        });
    };

    //------------------------------------------------------------------------------------------------------------------

    const onSelectPopulationProbabilityDelta = (delta) => {
        setStateSafely({
            populationProbabilityParameters: {
                ...state.populationProbabilityParameters,
                delta: delta,
            },
        });
    };

    const onSelectPopulationProbabilityMaritalStatus = (maritalStatus) => {
        setStateSafely({
            populationProbabilityParameters: {
                ...state.populationProbabilityParameters,
                maritalStatus: maritalStatus,
            },
        });
    };

    const onSelectPopulationProbabilityContraceptiveMethod = (contraceptiveMethod) => {
        setStateSafely({
            populationProbabilityParameters: {
                ...state.populationProbabilityParameters,
                contraceptiveMethod: contraceptiveMethod,
            },
        });
    };

    const onChangePopulationProbabilityRelativeToYear = (year, enforceRange) => {
        year = trimYearToRange(year, enforceRange);

        setStateSafely({
            populationProbabilityParameters: {
                ...state.populationProbabilityParameters,
                relativeToYear: year,
            },
        });
    };

    const onChangePopulationProbabilityYear = (year, enforceRange) => {
        year = trimYearToRange(year, enforceRange);

        setStateSafely({
            populationProbabilityParameters: {
                ...state.populationProbabilityParameters,
                year: year,
            },
        });
    };

    const onSelectPopulationProbabilityDirection = (direction) => {
        setStateSafely({
            populationProbabilityParameters: {
                ...state.populationProbabilityParameters,
                direction: direction,
            },
        });
    };

    const onChangePopulationProbabilityLevel = (level) => {
        level = trimToRange(level, 0, Number.MAX_VALUE);

        setStateSafely({
            populationProbabilityParameters: {
                ...state.populationProbabilityParameters,
                level: level,
            },
        });
    };

    //------------------------------------------------------------------------------------------------------------------

    const onSelectPopulationLevelDelta = (delta) => {
        setStateSafely({
            populationLevelParameters: {
                ...state.populationLevelParameters,
                delta: delta,
            },
        });
    };

    const onSelectPopulationLevelMaritalStatus = (maritalStatus) => {
        setStateSafely({
            populationLevelParameters: {
                ...state.populationLevelParameters,
                maritalStatus: maritalStatus,
            },
        });
    };

    const onSelectPopulationLevelContraceptiveMethod = (contraceptiveMethod) => {
        setStateSafely({
            populationLevelParameters: {
                ...state.populationLevelParameters,
                contraceptiveMethod: contraceptiveMethod,
            },
        });
    };

    const onChangePopulationLevelRelativeToYear = (year, enforceRange) => {
        year = trimYearToRange(year, enforceRange);

        setStateSafely({
            populationLevelParameters: {
                ...state.populationLevelParameters,
                relativeToYear: year,
            },
        });
    };

    const onChangePopulationLevelYear = (year, enforceRange) => {
        year = trimYearToRange(year, enforceRange);

        setStateSafely({
            populationLevelParameters: {
                ...state.populationLevelParameters,
                year: year,
            },
        });
    };

    const onChangePopulationLevelProbability = (probability) => {
        if (!probability) {
            probability = "";
        } else if (!probability.match(/^(0?(\.\d*)?|1)$/)) {
            probability = state.populationLevelParameters.probability;
        }

        setStateSafely({
            populationLevelParameters: {
                ...state.populationLevelParameters,
                probability: probability,
            },
        });
    };

    const onSelectPopulationLevelDirection = (direction) => {
        setStateSafely({
            populationLevelParameters: {
                ...state.populationLevelParameters,
                direction: direction,
            },
        });
    };

    //------------------------------------------------------------------------------------------------------------------

    const onCalculate = () => {
        setStateSafely({
            calculating: true,
        });

        const countryNumericCodeOrRegion = props.countryNumericCode || props.region;

        api.calculateTargets(props.runId, countryNumericCodeOrRegion, createPayload(state), signal.token)
            .then((response) => {
                if (!response) {
                    return;
                }

                setStateSafely({
                    calculatedAnswers: {
                        indicatorProbability: response.data.indicatorProbability,
                        indicatorLevel: response.data.indicatorLevel,
                        populationProbability: response.data.populationProbability,
                        populationLevel: response.data.populationLevel,
                    },
                });
            })
            .finally(() => {
                setStateSafely({
                    calculating: false,
                });
            });
    };

    const valid = (!!props.countryNumericCode || !!props.region) && Object.keys(createPayload(state)).length > 0;

    return (
        <Targets
            indicators={indicators}
            contraceptiveMethods={contraceptiveMethods}
            indicatorProbabilityParameters={state.indicatorProbabilityParameters}
            indicatorProbability={state.calculatedAnswers.indicatorProbability}
            indicatorLevelParameters={state.indicatorLevelParameters}
            indicatorLevel={state.calculatedAnswers.indicatorLevel}
            populationProbabilityParameters={state.populationProbabilityParameters}
            populationProbability={state.calculatedAnswers.populationProbability}
            populationLevelParameters={state.populationLevelParameters}
            populationLevel={state.calculatedAnswers.populationLevel}
            valid={valid}
            calculating={state.calculating}
            onSelectIndicatorProbabilityIndicator={onSelectIndicatorProbabilityIndicator}
            onSelectIndicatorProbabilityMaritalStatus={onSelectIndicatorProbabilityMaritalStatus}
            onChangeIndicatorProbabilityYear={onChangeIndicatorProbabilityYear}
            onSelectIndicatorProbabilityDirection={onSelectIndicatorProbabilityDirection}
            onChangeIndicatorProbabilityLevel={onChangeIndicatorProbabilityLevel}
            onSelectIndicatorLevelIndicator={onSelectIndicatorLevelIndicator}
            onSelectIndicatorLevelMaritalStatus={onSelectIndicatorLevelMaritalStatus}
            onChangeIndicatorLevelYear={onChangeIndicatorLevelYear}
            onChangeIndicatorLevelProbability={onChangeIndicatorLevelProbability}
            onSelectIndicatorLevelDirection={onSelectIndicatorLevelDirection}
            onSelectPopulationProbabilityDelta={onSelectPopulationProbabilityDelta}
            onSelectPopulationProbabilityMaritalStatus={onSelectPopulationProbabilityMaritalStatus}
            onChangePopulationProbabilityRelativeToYear={onChangePopulationProbabilityRelativeToYear}
            onSelectPopulationProbabilityContraceptiveMethod={onSelectPopulationProbabilityContraceptiveMethod}
            onChangePopulationProbabilityYear={onChangePopulationProbabilityYear}
            onSelectPopulationProbabilityDirection={onSelectPopulationProbabilityDirection}
            onChangePopulationProbabilityLevel={onChangePopulationProbabilityLevel}
            onSelectPopulationLevelDelta={onSelectPopulationLevelDelta}
            onSelectPopulationLevelMaritalStatus={onSelectPopulationLevelMaritalStatus}
            onChangePopulationLevelRelativeToYear={onChangePopulationLevelRelativeToYear}
            onSelectPopulationLevelContraceptiveMethod={onSelectPopulationLevelContraceptiveMethod}
            onChangePopulationLevelYear={onChangePopulationLevelYear}
            onChangePopulationLevelProbability={onChangePopulationLevelProbability}
            onSelectPopulationLevelDirection={onSelectPopulationLevelDirection}
            onCalculate={onCalculate}
        />
    );
};

TargetsContainer.propTypes = {
    runId: PropTypes.string,
    countryNumericCode: PropTypes.number,
    region: PropTypes.string,
};

export default TargetsContainer;
